import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
} from '@mui/material';

interface PortfolioTableViewSkeletonProps {
  rowCount?: number;
}

const PortfolioTableViewSkeleton: React.FC<PortfolioTableViewSkeletonProps> = ({
  rowCount = 6,
}) => {
  return (
    <Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(35, 35, 35, 0.8)"
              : "rgba(255, 255, 255, 0.9)",
          // REMOVED for performance: backdropFilter: "blur(20px)",
          border: (theme) =>
            theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.08)"
              : "1px solid rgba(0, 0, 0, 0.06)",
          borderRadius: 4,
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 4px 30px rgba(0, 0, 0, 0.3)"
              : "0 4px 30px rgba(0, 0, 0, 0.08)",
          overflow: "hidden",
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.02)"
                    : "rgba(0, 0, 0, 0.01)",
                "& .MuiTableCell-head": {
                  borderBottom: (theme) =>
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.06)"
                      : "1px solid rgba(0, 0, 0, 0.06)",
                },
              }}
            >
              {/* Company Column Header */}
              <TableCell
                sx={{
                  width: 60,
                  py: 2,
                  pl: 3,
                }}
                colSpan={2}
              >
                <Skeleton variant="text" width={80} height={20} />
              </TableCell>
              
              {/* Position Value Column Header */}
              <TableCell align="right" sx={{ py: 2 }}>
                <Skeleton variant="text" width={100} height={20} />
              </TableCell>
              
              {/* Shares Column Header */}
              <TableCell align="right" sx={{ py: 2 }}>
                <Skeleton variant="text" width={60} height={20} />
              </TableCell>
              
              {/* Total Return Column Header */}
              <TableCell align="right" sx={{ py: 2 }}>
                <Skeleton variant="text" width={90} height={20} />
              </TableCell>
              
              {/* Day Change Column Header */}
              <TableCell align="right" sx={{ py: 2 }}>
                <Skeleton variant="text" width={80} height={20} />
              </TableCell>
            </TableRow>
          </TableHead>
          
          <TableBody>
            {Array.from({ length: rowCount }, (_, index) => (
              <TableRow
                key={index}
                sx={{
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:last-child td": {
                    borderBottom: 0,
                  },
                  "& .MuiTableCell-root": {
                    borderBottom: (theme) =>
                      theme.palette.mode === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.04)"
                        : "1px solid rgba(0, 0, 0, 0.03)",
                  },
                }}
              >
                {/* Logo & Company Name & Ticker */}
                <TableCell sx={{ py: 2, pl: 3 }} colSpan={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={4}
                    paddingLeft={1}
                  >
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box>
                      <Skeleton variant="text" width={120} height={20} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width={60} height={16} />
                    </Box>
                  </Box>
                </TableCell>

                {/* Position Value */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Skeleton variant="text" width={100} height={20} />
                </TableCell>

                {/* Shares */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Skeleton variant="text" width={80} height={20} />
                </TableCell>

                {/* Total Return */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Box sx={{ textAlign: "right" }}>
                    <Skeleton variant="text" width={90} height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width={70} height={16} />
                  </Box>
                </TableCell>

                {/* Day Change */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Box sx={{ textAlign: "right" }}>
                    <Skeleton variant="text" width={80} height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width={60} height={16} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PortfolioTableViewSkeleton;